/**
 * CameraScannerModal — Full-screen camera overlay for scanning barcodes (Code 128, QR, EAN-13).
 *
 * Uses html5-qrcode with the rear camera (facingMode: "environment").
 * One-shot behaviour: scans a barcode → calls onScan(barcode) → auto-closes.
 * Handles camera-permission-denied and no-camera errors gracefully.
 * Cleans up the camera stream on close / unmount.
 */
import React, { useEffect, useRef, useCallback, useState } from 'react';
import { Html5Qrcode, Html5QrcodeScannerState, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import { X } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';

interface CameraScannerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onScan: (barcode: string) => void;
}

const SCANNER_ELEMENT_ID = 'camera-scanner-reader';

const CameraScannerModal: React.FC<CameraScannerModalProps> = ({ isOpen, onClose, onScan }) => {
  const { t } = useLanguage();
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const hasScannedRef = useRef(false);
  const [error, setError] = useState<string | null>(null);

  // Stop and clean up the scanner instance
  const stopScanner = useCallback(async () => {
    const scanner = scannerRef.current;
    if (!scanner) return;
    try {
      const state = scanner.getState();
      if (state === Html5QrcodeScannerState.SCANNING || state === Html5QrcodeScannerState.PAUSED) {
        await scanner.stop();
      }
    } catch {
      // Ignore stop errors — may already be stopped
    }
    try {
      scanner.clear();
    } catch {
      // Ignore clear errors
    }
    scannerRef.current = null;
  }, []);

  // Start the camera scanner when the modal opens
  useEffect(() => {
    if (!isOpen) return;

    hasScannedRef.current = false;
    setError(null);

    // Small delay to allow DOM element to render
    const timer = setTimeout(async () => {
      const element = document.getElementById(SCANNER_ELEMENT_ID);
      if (!element) return;

      // Check if navigator.mediaDevices is available (requires HTTPS on non-localhost)
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        const isInsecure = window.location.protocol === 'http:' && window.location.hostname !== 'localhost';
        setError(
          isInsecure
            ? 'Camera requires HTTPS. Please access the app via https:// or use localhost.'
            : (t('cameraScanner.noCamera') || 'No camera found on this device.')
        );
        return;
      }

      try {
        const scanner = new Html5Qrcode(SCANNER_ELEMENT_ID, {
          formatsToSupport: [
            Html5QrcodeSupportedFormats.CODE_128,
            Html5QrcodeSupportedFormats.QR_CODE,
            Html5QrcodeSupportedFormats.EAN_13,
          ],
          verbose: false,
        });
        scannerRef.current = scanner;

        await scanner.start(
          { facingMode: 'environment' },
          {
            fps: 10,
            qrbox: (viewfinderWidth, viewfinderHeight) => {
              // Make qrbox responsive — 80% of the smaller dimension, capped
              const w = Math.min(280, Math.floor(viewfinderWidth * 0.8));
              const h = Math.min(150, Math.floor(viewfinderHeight * 0.4));
              return { width: Math.max(100, w), height: Math.max(80, h) };
            },
          },
          (decodedText) => {
            // One-shot: only fire once per modal open
            if (hasScannedRef.current) return;
            hasScannedRef.current = true;

            // Stop scanner, call the callback, and close
            stopScanner().then(() => {
              onScan(decodedText);
              onClose();
            });
          },
          () => {
            // QR scan failure callback (called every frame with no match) — ignore
          }
        );
      } catch (err: any) {
        console.error('[CameraScanner] Failed to start:', err);
        const msg = err?.toString?.() || '';
        if (msg.includes('NotAllowedError') || msg.includes('Permission')) {
          setError(t('cameraScanner.permissionDenied') || 'Camera permission denied. Please allow camera access in your browser settings.');
        } else if (msg.includes('NotFoundError') || msg.includes('no camera') || msg.includes('Requested device not found')) {
          setError(t('cameraScanner.noCamera') || 'No camera found on this device.');
        } else if (msg.includes('NotReadableError') || msg.includes('Could not start video source')) {
          setError('Camera is in use by another app. Please close other apps using the camera and try again.');
        } else if (window.location.protocol === 'http:' && window.location.hostname !== 'localhost') {
          // iOS Safari requires HTTPS for camera access
          setError('Camera requires HTTPS. Please access the app via https:// or use localhost.');
        } else {
          setError(`${t('cameraScanner.error') || 'Failed to start camera.'} (${msg.slice(0, 100)})`);
        }
      }
    }, 300);

    return () => {
      clearTimeout(timer);
      stopScanner();
    };
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Also clean up on unmount
  useEffect(() => {
    return () => {
      stopScanner();
    };
  }, [stopScanner]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] bg-black flex flex-col">
      {/* Top bar with close button */}
      <div className="relative z-10 flex items-center justify-between px-4 py-3 bg-black/70">
        <h2 className="text-white text-lg font-semibold">
          {t('cameraScanner.title') || 'Scan Barcode'}
        </h2>
        <button
          onClick={() => {
            stopScanner();
            onClose();
          }}
          className="p-2 rounded-full bg-white/20 hover:bg-white/30 active:bg-white/40 transition-colors"
        >
          <X className="w-6 h-6 text-white" />
        </button>
      </div>

      {/* Camera feed area */}
      <div className="flex-1 flex flex-col items-center justify-center relative">
        {/* The html5-qrcode lib renders the video inside this div */}
        <div id={SCANNER_ELEMENT_ID} className="w-full max-w-md" />

        {/* Instruction text below the viewfinder */}
        {!error && (
          <p className="text-white/80 text-sm mt-4 text-center px-4">
            {t('cameraScanner.instruction') || 'Point the camera at a barcode'}
          </p>
        )}

        {/* Error display */}
        {error && (
          <div className="mx-4 mt-4 p-4 bg-red-900/60 border border-red-500/50 rounded-xl text-center">
            <p className="text-red-200 text-sm">{error}</p>
            <button
              onClick={() => {
                stopScanner();
                onClose();
              }}
              className="mt-3 px-6 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg text-sm font-medium transition-colors"
            >
              {t('common.close') || 'Close'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CameraScannerModal;
